"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import IdentityVerificationButton from "@/components/IdentityVerificationButton";
import type { IdentityProvider } from "@/lib/identity-types";

const VERIFIED_IDENTITY_KEY = "lighthost.identity.verified";

type Props = {
  availableProviders: IdentityProvider[];
};

const inputClassName =
  "mt-2 h-12 w-full min-w-0 max-w-full rounded-lg border border-[#dcd7e1] bg-white px-4 text-[15px] text-[#19171f] outline-none transition placeholder:text-[#98929f] hover:border-[#c7bfce] focus:border-[#8F4FE8] focus:ring-4 focus:ring-[#F4ECFF]";

const checkboxClassName =
  "mt-0.5 h-4 w-4 shrink-0 accent-[#8F4FE8]";

export default function RegisterForm({ availableProviders }: Props) {
  const router = useRouter();
  const formRef = useRef<HTMLFormElement>(null);

  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [identityVerificationId, setIdentityVerificationId] = useState("");

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeAge, setAgreeAge] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);

  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [identityPickerOpen, setIdentityPickerOpen] = useState(false);
  const requiredAgreed = agreeTerms && agreePrivacy && agreeAge;

  useEffect(() => {
    const stored = sessionStorage.getItem(VERIFIED_IDENTITY_KEY);
    if (!stored) return;
    try {
      const value = JSON.parse(stored) as {
        email?: string;
        identityVerificationId?: string;
      };
      if (value.email && value.identityVerificationId) {
        setEmail(value.email);
        setEmailVerified(true);
        setIdentityVerificationId(value.identityVerificationId);
        setMessage("본인인증이 완료되었습니다. 나머지 가입 정보를 입력해주세요.");
      }
    } catch {
      sessionStorage.removeItem(VERIFIED_IDENTITY_KEY);
    }
  }, []);

  useEffect(() => {
    if (!identityPickerOpen) return;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIdentityPickerOpen(false);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [identityPickerOpen]);

  function handleOpenIdentityPicker() {
    setError("");
    setMessage("");

    if (!requiredAgreed) {
      setError("필수 약관에 모두 동의해주세요.");
      return;
    }

    if (!formRef.current?.reportValidity()) return;

    if (!emailVerified) {
      setError("이메일 인증을 먼저 완료해주세요.");
      return;
    }

    setIdentityPickerOpen(true);
  }

  async function handleSendEmailCode() {
    setError("");
    setMessage("");

    if (!email) {
      setError("이메일을 입력해주세요.");
      return;
    }

    setSendingCode(true);

    const response = await fetch("/api/email/send-code", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });

    setSendingCode(false);
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      setError(data?.message || "인증코드 발송에 실패했습니다.");
      return;
    }

    setEmailVerified(false);
    setMessage("인증코드를 이메일로 발송했습니다.");
  }

  async function handleVerifyEmailCode() {
    setError("");
    setMessage("");

    if (!email || !emailCode) {
      setError("이메일과 인증코드를 입력해주세요.");
      return;
    }

    setVerifyingCode(true);

    const response = await fetch("/api/email/verify-code", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, code: emailCode }),
    });

    setVerifyingCode(false);
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      setError(data?.message || "인증코드가 올바르지 않습니다.");
      return;
    }

    setEmailVerified(true);
    setMessage("이메일 인증이 완료되었습니다.");
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setError("");
    setMessage("");

    if (!agreeTerms || !agreePrivacy || !agreeAge) {
      setError("필수 약관에 모두 동의해주세요.");
      return;
    }

    if (!emailVerified) {
      setError("이메일 인증을 완료해주세요.");
      return;
    }

    if (!identityVerificationId) {
      setError("KCP 또는 KG이니시스 본인인증을 완료해주세요.");
      return;
    }

    setLoading(true);

    const response = await fetch("/api/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        username,
        name,
        identityVerificationId,
        agreeMarketing,
        agreeTerms,
        agreePrivacy,
        agreeAge,
      }),
    });

    setLoading(false);
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      setError(data?.message || "회원가입에 실패했습니다.");
      return;
    }

    alert("회원가입이 완료되었습니다. 로그인해주세요.");
    sessionStorage.removeItem(VERIFIED_IDENTITY_KEY);
    router.push("/login");
    router.refresh();
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="min-w-0 w-full">
      <section className="border-b border-[#e5e1e9] py-8">
        <SectionHeading
          number="01"
          title="약관 동의"
          description="서비스 이용에 필요한 내용을 먼저 확인해주세요."
        />

        <label className="mt-6 flex cursor-pointer items-center gap-3 border-b border-[#e5e1e9] bg-[#faf9fb] px-4 py-4 text-sm font-black text-[#302c35]">
          <input
            type="checkbox"
            checked={requiredAgreed}
            onChange={(event) => {
              setAgreeTerms(event.target.checked);
              setAgreePrivacy(event.target.checked);
              setAgreeAge(event.target.checked);
            }}
            className={checkboxClassName}
          />
          필수 약관에 모두 동의합니다.
        </label>

        <div className="space-y-4 px-1 py-5 text-sm text-[#5f5966]">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={agreeTerms}
              onChange={(event) => setAgreeTerms(event.target.checked)}
              className={checkboxClassName}
            />
            <span>
              <strong className="text-[#8F4FE8]">[필수]</strong>{" "}
              <Link href="/terms" target="_blank" className="font-bold underline underline-offset-4">
                LightHost 이용약관
              </Link>
              에 동의합니다.
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={agreePrivacy}
              onChange={(event) => setAgreePrivacy(event.target.checked)}
              className={checkboxClassName}
            />
            <span>
              <strong className="text-[#8F4FE8]">[필수]</strong>{" "}
              <Link href="/privacy" target="_blank" className="font-bold underline underline-offset-4">
                개인정보처리방침
              </Link>
              에 동의합니다.
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={agreeAge}
              onChange={(event) => setAgreeAge(event.target.checked)}
              className={checkboxClassName}
            />
            <span><strong className="text-[#8F4FE8]">[필수]</strong> 만 14세 이상입니다.</span>
          </label>
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={agreeMarketing}
              onChange={(event) => setAgreeMarketing(event.target.checked)}
              className={checkboxClassName}
            />
            <span><strong className="text-[#77717e]">[선택]</strong> 이벤트, 할인, 서비스 안내 수신에 동의합니다.</span>
          </label>
        </div>
      </section>

      <section className="border-b border-[#e5e1e9] py-8">
        <SectionHeading
          number="02"
          title="계정 정보"
          description="로그인과 서비스 관리에 사용할 정보를 입력해주세요."
        />

        <div className="mt-7 grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="register-username" className="text-sm font-bold text-[#302c35]">
              아이디
            </label>
            <input
              id="register-username"
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
              placeholder="lightuser"
              value={username}
              onChange={(event) => setUsername(event.target.value.toLowerCase())}
              className={inputClassName}
              minLength={4}
              maxLength={20}
              pattern="[a-z0-9_-]{4,20}"
              required
            />
            <p className="mt-2 text-xs leading-5 text-[#77717e]">
              영문 소문자, 숫자, _, - 조합 4~20자
            </p>
          </div>

          <div>
            <label htmlFor="register-name" className="text-sm font-bold text-[#302c35]">
              이름
            </label>
            <input
              id="register-name"
              type="text"
              autoComplete="name"
              placeholder="홍길동"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className={inputClassName}
              required
            />
          </div>
        </div>

        <div className="mt-5">
          <label htmlFor="register-password" className="text-sm font-bold text-[#302c35]">
            비밀번호
          </label>
          <div className="relative">
            <input
              id="register-password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="8자 이상 입력"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className={`${inputClassName} pr-12`}
              minLength={8}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="absolute right-1 top-[9px] flex h-10 w-10 items-center justify-center rounded-lg text-[#77717e] transition hover:bg-[#F4ECFF] hover:text-[#7B3FD1]"
              aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </section>

      <section className="border-b border-[#e5e1e9] py-8">
        <SectionHeading
          number="03"
          title="이메일 인증"
          description="주문 및 서비스 안내를 받을 이메일을 확인합니다."
        />

        <div className="mt-7">
          <label htmlFor="register-email" className="text-sm font-bold text-[#302c35]">
            이메일
          </label>
          <div className="mt-2 grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_8.75rem]">
            <input
              id="register-email"
              type="email"
              autoComplete="email"
              placeholder="example@email.com"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setEmailVerified(false);
                setEmailCode("");
                setIdentityVerificationId("");
                sessionStorage.removeItem(VERIFIED_IDENTITY_KEY);
              }}
              className={`${inputClassName} mt-0`}
              required
            />
            <button
              type="button"
              onClick={handleSendEmailCode}
              disabled={sendingCode || !email || emailVerified}
              className="h-12 w-full rounded-lg bg-[#8F4FE8] px-4 text-sm font-bold text-white transition hover:bg-[#7B3FD1] disabled:cursor-not-allowed disabled:bg-[#ddd7e5] disabled:text-[#8a8490]"
            >
              {emailVerified ? "인증 완료" : sendingCode ? "발송 중" : "인증번호 받기"}
            </button>
          </div>
        </div>

        <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_8.75rem]">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-label="이메일 인증번호"
            placeholder="인증번호 6자리"
            value={emailCode}
            onChange={(event) => {
              setEmailCode(event.target.value.replace(/\D/g, "").slice(0, 6));
              setEmailVerified(false);
            }}
            className={`${inputClassName} mt-0`}
            maxLength={6}
            disabled={emailVerified}
          />
          <button
            type="button"
            onClick={handleVerifyEmailCode}
            disabled={verifyingCode || !email || emailCode.length !== 6 || emailVerified}
            className="h-12 w-full rounded-lg border border-[#cfc8d6] bg-white px-4 text-sm font-bold text-[#302c35] transition hover:border-[#8F4FE8] hover:text-[#7B3FD1] disabled:cursor-not-allowed disabled:border-[#e5e1e9] disabled:text-[#aaa4af]"
          >
            {emailVerified ? "확인됨" : verifyingCode ? "확인 중" : "인증번호 확인"}
          </button>
        </div>

        {emailVerified && (
          <p className="mt-3 text-sm font-bold text-[#047857]">
            이메일 인증이 완료되었습니다.
          </p>
        )}
      </section>

      {error && (
        <p role="alert" className="mt-8 border-l-4 border-[#c24156] bg-[#fff1f3] px-4 py-3 text-sm font-bold text-[#a32035]">
          {error}
        </p>
      )}

      {message && (
        <p role="status" className="mt-8 border-l-4 border-[#059669] bg-[#ecfdf5] px-4 py-3 text-sm font-bold text-[#047857]">
          {message}
        </p>
      )}

      {identityVerificationId ? (
        <button
          type="submit"
          disabled={loading}
          className="mt-8 flex h-14 w-full items-center justify-center rounded-lg bg-[#8F4FE8] px-5 font-black text-white transition hover:bg-[#7B3FD1] disabled:cursor-not-allowed disabled:bg-[#ddd7e5] disabled:text-[#8a8490]"
        >
          {loading ? "가입 처리 중..." : "회원가입 완료하기"}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleOpenIdentityPicker}
          className="mt-8 flex h-14 w-full items-center justify-center rounded-lg bg-[#8F4FE8] px-5 font-black text-white transition hover:bg-[#7B3FD1]"
        >
          본인인증하러 가기
        </button>
      )}

      <p className="mt-3 text-center text-xs leading-5 text-[#77717e]">
        본인인증 완료 후 회원가입을 마칠 수 있습니다.
      </p>

      <p className="mt-5 text-center text-sm text-[#77717e] lg:hidden">
        이미 계정이 있나요?{" "}
        <Link href="/login" className="font-bold text-[#8F4FE8] hover:text-[#7B3FD1]">
          로그인
        </Link>
      </p>

      {identityPickerOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-5 py-8">
          <button
            type="button"
            className="absolute inset-0 h-full w-full bg-[#19171f]/45 backdrop-blur-[2px]"
            onClick={() => setIdentityPickerOpen(false)}
            aria-label="본인인증 방법 선택 닫기"
          />
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="identity-picker-title"
            className="relative z-10 w-full max-w-lg border border-[#e5e1e9] bg-white p-6 shadow-[0_24px_80px_rgba(25,23,31,0.24)] sm:p-8"
          >
            <button
              type="button"
              onClick={() => setIdentityPickerOpen(false)}
              className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-lg text-[#77717e] transition hover:bg-[#f3f0f6] hover:text-[#19171f]"
              aria-label="닫기"
              autoFocus
            >
              <X className="h-5 w-5" />
            </button>

            <p className="text-sm font-black text-[#8F4FE8]">본인 확인</p>
            <h3 id="identity-picker-title" className="mt-2 pr-12 text-2xl font-black text-[#19171f]">
              인증 방법을 선택해주세요
            </h3>
            <p className="mt-3 text-sm leading-6 text-[#6b6873]">
              휴대폰 본인인증 또는 민간인증서 중 하나만 완료하면 됩니다.
            </p>

            <div className="mt-6">
              <IdentityVerificationButton
                email={email}
                emailVerified={emailVerified}
                purpose="REGISTRATION"
                redirectPath="/register/identity/callback"
                verified={false}
                availableProviders={availableProviders}
                onError={(verificationError) => {
                  setIdentityPickerOpen(false);
                  setError(verificationError);
                }}
                onVerified={(verificationId) => {
                  setIdentityVerificationId(verificationId);
                  setIdentityPickerOpen(false);
                  setError("");
                  setMessage("본인인증이 완료되었습니다. 회원가입을 완료해주세요.");
                }}
              />
            </div>

            <p className="mt-5 text-xs leading-5 text-[#77717e]">
              휴대폰 번호와 생년월일은 직접 입력받지 않고 본인확인기관의 인증 결과로 확인합니다.
            </p>
          </section>
        </div>
      )}
    </form>
  );
}

function SectionHeading({
  number,
  title,
  description,
}: {
  number: string;
  title: string;
  description: string;
}) {
  return (
    <div>
      <div className="flex items-baseline gap-3">
        <span className="text-xs font-black text-[#8F4FE8]">{number}</span>
        <h3 className="text-lg font-black text-[#19171f]">{title}</h3>
      </div>
      <p className="mt-1 pl-7 text-sm leading-6 text-[#77717e]">{description}</p>
    </div>
  );
}
